import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASELINE } from '../../util';

const ContentRegeneratorModal = ({
    isOpen,
    onClose,
    htmlContent,
    onContentRegenerated
}) => {
    // States
    const [originalContent, setOriginalContent] = useState(htmlContent);
    const [generatedContent, setGeneratedContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');

    // Refs for handling modal content
    const modalRef = useRef(null);

    // Update originalContent when htmlContent prop changes
    useEffect(() => {
        setOriginalContent(htmlContent);
    }, [htmlContent]);

    // Generate new content using AI
    const handleGenerateContent = async () => {
        setIsLoading(true);
        setProcessingStatus('Generating new content...');
        setGeneratedContent('');

        try {
            // Default prompt for regenerating content
            const regenerationPrompt = `
            Please rewrite the following content for my SAT class next year. I need:
            - Different wording while preserving the exact same meaning.
            - Simpler, more concise language where possible, avoiding unnecessarily lengthy rewrites.
            - Updated practice examples and problems that fit the SAT theme.
            - Restructured sentences that maintain the core ideas and original educational concepts.
            - Grammar and educational formatting that align with SAT standards.
            - A complete rephrasing to avoid copyright issues—ensure that all text is fully rewritten so that no copyrighted material is directly replicated.
            - If certain topics are already clear and concise, they can remain largely unchanged.
            
            Original content:
            --------------------------------------------------
            ${originalContent}
            --------------------------------------------------
            
            Please provide only the rewritten content without any introduction or explanation. Maintain all HTML formatting, math expressions, and structural elements from the original.
            `;            

            // Call AI API
            const response = await axios.post(`${BASELINE}askai`, {
                prompt: regenerationPrompt,
                type: "content"
            });

            if (response.data && response.data.message) {
                const answer = response.data.message.trim();
                setGeneratedContent(answer);
                setProcessingStatus('Content regenerated successfully!');
            } else {
                console.error('Unexpected response structure:', response.data);
                setProcessingStatus('Error: Unexpected response from AI service');
            }
        } catch (error) {
            console.error('Error generating content:', error);
            setProcessingStatus(`Error: ${error.message || 'Failed to generate content'}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Copy generated content to clipboard
    const handleCopyContent = () => {
        if (generatedContent) {
            navigator.clipboard.writeText(generatedContent)
                .then(() => {
                    setProcessingStatus('Content copied to clipboard!');
                    setTimeout(() => {
                        setProcessingStatus('');
                    }, 2000);
                })
                .catch(err => {
                    setProcessingStatus(`Failed to copy: ${err.message}`);
                });
        }
    };

    // Clear generated content
    const handleClearContent = () => {
        setGeneratedContent('');
        setProcessingStatus('Content cleared');
        setTimeout(() => {
            setProcessingStatus('');
        }, 1500);
    };

    // Apply generated content to parent component
    const handleApplyContent = () => {
        if (generatedContent && typeof onContentRegenerated === 'function') {
            onContentRegenerated(generatedContent);
            setProcessingStatus('New content applied to editor!');
            setTimeout(() => {
                onClose();
            }, 1500);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="text-black fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10 p-4 overflow-auto">
            <div
                ref={modalRef}
                className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-black text-2xl font-bold">Content Regenerator</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Status Message */}
                {processingStatus && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-blue-700 flex items-center">
                            {isLoading && (
                                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {processingStatus}
                        </p>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-6">
                    <button
                        onClick={handleGenerateContent}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                        disabled={isLoading || !originalContent}
                    >
                        {isLoading ? 'Generating...' : 'Generate New Content'}
                    </button>

                    <button
                        onClick={handleCopyContent}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition"
                        disabled={!generatedContent}
                    >
                        Copy to Clipboard
                    </button>

                    <button
                        onClick={handleClearContent}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                        disabled={!generatedContent}
                    >
                        Clear
                    </button>

                    <button
                        onClick={handleApplyContent}
                        className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition"
                        disabled={!generatedContent}
                    >
                        Apply to Editor
                    </button>
                </div>

                {/* Content Comparison */}
                <div className="text-black grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Original Content */}
                    <div className="border rounded p-4">
                        <h3 className="text-black font-bold text-lg mb-2">Original Content</h3>
                        <div className="bg-gray-50 p-3 rounded h-[60vh] overflow-auto">
                            <div dangerouslySetInnerHTML={{ __html: originalContent }} />
                        </div>
                    </div>

                    {/* Generated Content */}
                    <div className="border rounded p-4">
                        <h3 className="text-black font-bold text-lg mb-2">Regenerated Content</h3>
                        <div className="bg-gray-50 p-3 rounded h-[60vh] overflow-auto">
                            {generatedContent ? (
                                <div dangerouslySetInnerHTML={{ __html: generatedContent }} />
                            ) : (
                                <p className="text-gray-500 italic">
                                    {isLoading ? 'Generating content...' : 'Click "Generate New Content" to create regenerated content'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContentRegeneratorModal;